import { useMemo } from "react";
import { Checkbox } from "@volca/ui/components/ui/checkbox";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import type {
  IAiSpreadsheetCell,
  IAiSpreadsheetRegionColumnSetting,
} from "@/payroll/ai-script/types";
import { getAiSpreadsheetCellDisplayValue } from "./cell-display-value";
import type { SpreadsheetRegionDraft } from "./spreadsheet-region";
import { formatColumnLetter } from "./utils";

const CONTROL_GRID =
  "grid grid-cols-[1fr_64px_64px] items-center gap-2 px-3 py-2";

type ColumnControlKey = "filterable" | "sortable";

const DEFAULT_COLUMN_SETTING: IAiSpreadsheetRegionColumnSetting = {
  filterable: true,
  sortable: true,
  declared_type: null,
};

interface SpreadsheetRegionColumnControlsProps {
  cells: IAiSpreadsheetCell[];
  draft: SpreadsheetRegionDraft;
  onChange: (draft: SpreadsheetRegionDraft) => void;
}

export const SpreadsheetRegionColumnControls = ({
  cells,
  draft,
  onChange,
}: SpreadsheetRegionColumnControlsProps) => {
  const columnNumbers = Array.from(
    { length: draft.end_col - draft.start_col + 1 },
    (_, offset) => draft.start_col + offset,
  );

  // A sheet can contain many cells. Rebuild the header lookup only when the
  // selected header row or loaded cells change.
  const headerLabels = useMemo(
    () =>
      new Map(
        cells
          .filter((cell) => cell.row === draft.header_row)
          .map((cell) => [
            cell.col,
            getAiSpreadsheetCellDisplayValue(cell).trim(),
          ]),
      ),
    [cells, draft.header_row],
  );

  const changeEveryColumn = (key: ColumnControlKey, checked: boolean) =>
    onChange({
      ...draft,
      column_settings: Object.fromEntries(
        columnNumbers.map((column) => {
          const columnKey = String(column);
          return [
            columnKey,
            {
              ...DEFAULT_COLUMN_SETTING,
              ...draft.column_settings[columnKey],
              [key]: checked,
            },
          ];
        }),
      ),
    });

  const changeColumn = (
    column: number,
    key: ColumnControlKey,
    checked: boolean,
  ) =>
    onChange({
      ...draft,
      column_settings: {
        ...draft.column_settings,
        [String(column)]: {
          ...DEFAULT_COLUMN_SETTING,
          ...draft.column_settings[String(column)],
          [key]: checked,
        },
      },
    });

  const allEnabled = {
    filterable: columnNumbers.every(
      (column) => draft.column_settings[String(column)]?.filterable,
    ),
    sortable: columnNumbers.every(
      (column) => draft.column_settings[String(column)]?.sortable,
    ),
  };

  return (
    <Stack className="w-full" align="start" gap={0}>
      <Stack
        gap={4}
        direction="row"
        align="center"
        justify="between"
        className="w-full pb-2"
      >
        <Text variant="label">Column controls</Text>
        <Text variant="caption" tone="muted">
          {columnNumbers.length} columns
        </Text>
      </Stack>

      <Stack
        gap={0}
        // Override Stack's start alignment so every control row has one width.
        align="stretch"
        className="w-full overflow-hidden rounded-lg border border-line"
      >
        <div className={cn(CONTROL_GRID, "bg-surface-sunken")}>
          <span className="text-xs font-medium tracking-widest text-subtle uppercase">
            Column
          </span>
          <span className="text-center text-xs font-medium tracking-widest text-subtle uppercase">
            Filter
          </span>
          <span className="text-center text-xs font-medium tracking-widest text-subtle uppercase">
            Sort
          </span>
        </div>

        <div className={cn(CONTROL_GRID, "border-t border-line")}>
          <Text variant="caption" tone="muted">
            All columns
          </Text>
          {(["filterable", "sortable"] as const).map((key) => (
            <Stack
              align="start"
              key={key}
              direction="row"
              justify="center"
              gap={0}
            >
              <Checkbox
                aria-label={`${key === "filterable" ? "Filter" : "Sort"} on every column`}
                checked={allEnabled[key]}
                onCheckedChange={(checked) =>
                  changeEveryColumn(key, checked === true)
                }
              />
            </Stack>
          ))}
        </div>

        {columnNumbers.map((column) => {
          const setting = draft.column_settings[String(column)];
          const label = headerLabels.get(column);
          const columnName = formatColumnLetter(column - 1);

          return (
            <div
              key={column}
              className={cn(CONTROL_GRID, "border-t border-line")}
            >
              <Stack direction="row" align="center" gap={2} className="min-w-0">
                <span className="w-6 shrink-0 font-mono text-xs text-subtle">
                  {columnName}
                </span>
                <span className="truncate text-xs" title={label || undefined}>
                  {label || <span className="text-subtle">(no header)</span>}
                </span>
              </Stack>
              {(["filterable", "sortable"] as const).map((key) => (
                <Stack
                  align="start"
                  key={key}
                  direction="row"
                  justify="center"
                  gap={0}
                >
                  <Checkbox
                    aria-label={`${key === "filterable" ? "Filter" : "Sort"} on ${label || columnName}`}
                    checked={setting?.[key] ?? false}
                    onCheckedChange={(checked) =>
                      changeColumn(column, key, checked === true)
                    }
                  />
                </Stack>
              ))}
            </div>
          );
        })}
      </Stack>
    </Stack>
  );
};
